/* eslint-disable @typescript-eslint/no-explicit-any */
import ColorPicker from '@/shared/components/ColorPicker/ColorPicker'
import ImageUploadButton from '@/shared/components/ImageUploadButton/ImageUploadButton'
import { useTranslation } from 'react-i18next'
import Button from '@/shared/components/Button/Button'
import Alert from '@/shared/components/Alert/Alert'
import { useColorStore } from '@/features/mobile-app/store/useColorStore'

import useMobileAppManagementQuery from '@/features/mobile-app/hooks/useMobileAppManagementQuery'
import { useEffect } from 'react'
import { hsvaToRgbString, rgbaToHsva } from '@uiw/color-convert'
import styles from './MobileAppForm.module.scss'
import { useImageSlots } from '@/shared/store/useImageSlots'
import IMAGE_SLOTS_KEYS from '@/shared/store/imageSlots.model'
import TextField from '@/shared/components/TextField/TextField'
import { useMobileAppDataStore } from '@/features/mobile-app/store/useMobileAppDataStore'
import { colorStringToHsva } from '@/features/mobile-app/utils/colors'

function MobileAppForm() {
  const {
    primary,
    secondary,
    tertiary,
    background,
    setPrimary,
    setSecondary,
    setTertiary,
    setBackground,
    applyColors
  } = useColorStore()

  const {
    name,
    websiteText,
    websiteUrl,
    setName,
    setWebsiteText,
    setWebsiteUrl,
    reset
  } = useMobileAppDataStore()

  const { t } = useTranslation()

  const {
    backgroundImage,
    mobileAppData,
    postBackgroundImage,
    extractColorsSchemeFromImage,
    isExtractingColors,
    hasExtractedColors,
    hasExtractColorsFailed,
    resetExtractColorsState,
    postMobileAppData
  } = useMobileAppManagementQuery()

  const { clearAll } = useImageSlots(s => s)

  const newBackgroundImage = useImageSlots(
    s => s.slots[IMAGE_SLOTS_KEYS.MOBILE_APP_BACKGROUND]
  )

  const backgroundImagePreviewUrl =
    newBackgroundImage?.croppedUrl ||
    (backgroundImage.data?.imageUrl
      ? `${backgroundImage.data.imageUrl}?t=${backgroundImage.data.version}&alt=media`
      : '')

  const mobileAppColorSlot = useImageSlots(
    s => s.slots[IMAGE_SLOTS_KEYS.MOBILE_APP_COLOR_SELECTOR]
  )

  useEffect(() => {
    const extract = async () => {
      if (mobileAppColorSlot?.croppedBlob) {
        const croppedImage = new File(
          [mobileAppColorSlot.croppedBlob],
          'croppedImage'
        )

        try {
          const colorScheme = await extractColorsSchemeFromImage(croppedImage)

          const newPrimary = { ...rgbaToHsva(colorScheme[0]), a: 1 }
          const newSecondary = { ...rgbaToHsva(colorScheme[1]), a: 1 }
          const newTertiary = { ...rgbaToHsva(colorScheme[2]), a: 1 }
          const newBackground = { ...rgbaToHsva(colorScheme[3]), a: 1 }

          setPrimary(newPrimary)
          setSecondary(newSecondary)
          setTertiary(newTertiary)
          setBackground(newBackground)
          applyColors()
        } catch (error) {
          console.error('❌ Failed to extract colors:', error)
        }
      }
    }

    extract()
  }, [
    mobileAppColorSlot?.croppedBlob,
    extractColorsSchemeFromImage,
    setPrimary,
    setSecondary,
    setTertiary,
    setBackground,
    applyColors
  ])

  /*
    The branding document is partial by nature: the backend leaves out `venue`
    and `color` until something has been saved and drops the empty fields
    inside them, so every level is checked before it is read. It always returns
    `tipFeeBps`, which is why counting the response's keys said "there is
    branding here" for a venue that had none and then read `name` off an
    undefined `venue`. Whatever is missing keeps the store's default rather
    than being blanked out.
  */
  useEffect(() => {
    const branding = mobileAppData.data
    if (!branding) return

    const { venue, color } = branding

    if (venue?.name) setName(venue.name)
    if (venue?.websiteText) setWebsiteText(venue.websiteText)
    if (venue?.websiteUrl) setWebsiteUrl(venue.websiteUrl)

    if (!color) return

    if (color.primary) setPrimary(colorStringToHsva(color.primary))
    if (color.secondary) setSecondary(colorStringToHsva(color.secondary))
    if (color.tertiary) setTertiary(colorStringToHsva(color.tertiary))
    if (color.background) setBackground(colorStringToHsva(color.background))

    applyColors()
  }, [mobileAppData.data])

  /*
    Same status banner the automatic translation uses (AutoTranslateFormEffect):
    a call that rewrites fields the user is looking at has to say that it is
    running, or the picture lands in the tile and nothing appears to happen for
    as long as the service takes. Info while it runs, then the outcome, which
    stays until it is dismissed or another photo is dropped in.
  */
  const extractColorsStatus = (() => {
    if (isExtractingColors) return 'info' as const
    if (hasExtractColorsFailed) return 'error' as const
    if (hasExtractedColors) return 'success' as const
    return null
  })()

  const EXTRACT_COLORS_MESSAGES = {
    info: 'MOBILE_APP.FORM.EXTRACT_COLORS.IN_PROGRESS',
    success: 'MOBILE_APP.FORM.EXTRACT_COLORS.SUCCESS',
    error: 'MOBILE_APP.FORM.EXTRACT_COLORS.ERROR'
  }

  const handleSubmit = () => {
    const mobileAppDataPayload = {
      venue: {
        name,
        websiteText,
        websiteUrl
      },
      color: {
        primary: hsvaToRgbString(primary),
        secondary: hsvaToRgbString(secondary),
        tertiary: hsvaToRgbString(tertiary),
        background: hsvaToRgbString(background)
      }
    }
    postMobileAppData(mobileAppDataPayload)
    if (newBackgroundImage?.originalFile) {
      postBackgroundImage({ file: newBackgroundImage.originalFile })
    }
  }

  return (
    <div className={styles.mobileAppForm}>
      <div className={styles.mobileAppFormWrapper}>
        <p className={`title-bold-medium ${styles.mobileAppFormTitle}`}>
          {t('MOBILE_APP.FORM.TITLE')}
        </p>
        <p className={`body-medium ${styles.mobileAppDescriptionText}`}>
          {t('MOBILE_APP.FORM.DESCRIPTION')}
        </p>
        <div className={styles.mobileAppVenuInformationWrapper}>
          <div className={styles.mobileAppFormContainer}>
            <h1
              className={`title-bold-medium ${styles.mobileAppFormGroupTitle}`}
            >
              {t('MOBILE_APP.FORM.VENUES_INFORMATION')}
            </h1>
            <div
              className={`${styles.mobileAppFormGroup} ${styles.restaurantInformationContainer}`}
            >
              <TextField
                label={t('MOBILE_APP.FORM.VENUES_INFORMATIONS.VENUE_NAME')}
                placeholder={t(
                  'MOBILE_APP.FORM.VENUES_INFORMATIONS.VENUE_NAME.PLACEHOLDER'
                )}
                focused
                onChange={e => setName(e.target.value)}
                value={name}
              />
              <TextField
                label={t('MOBILE_APP.FORM.VENUES_INFORMATIONS.VENUE_PAGE_TEXT')}
                placeholder={t(
                  'MOBILE_APP.FORM.VENUES_INFORMATIONS.VENUE_PAGE_TEXT.PLACEHOLDER'
                )}
                focused
                onChange={e => setWebsiteText(e.target.value)}
                value={websiteText}
              />
              <TextField
                label={t('MOBILE_APP.FORM.VENUES_INFORMATIONS.VENUE_PAGE_URL')}
                placeholder={t(
                  'MOBILE_APP.FORM.VENUES_INFORMATIONS.VENUE_PAGE_URL.PLACEHOLDER'
                )}
                focused
                onChange={e => setWebsiteUrl(e.target.value)}
                value={websiteUrl}
              />
            </div>
          </div>
          <div className={styles.mobileAppPicturesContainer}>
            <h1
              className={`title-bold-medium ${styles.mobileAppFormGroupTitle}`}
            >
              {t('MOBILE_APP.FORM.VENUES_PICTURES')}
            </h1>
            {/* No logo here: it is the venue's, set in Settings > Restaurant,
                and the preview beside this form reads it from there. */}
            <div className={styles.mobileAppFormGroup}>
              <ImageUploadButton
                imageShape="rectangle"
                slotId={IMAGE_SLOTS_KEYS.MOBILE_APP_BACKGROUND}
                label={t('MOBILE_APP.FORM.BACKGROUND')}
                imageUrl={backgroundImagePreviewUrl}
                imageSize={{ width: 608, height: 376 }}
                stencilSize={{ width: 432, height: 200 }}
              />
            </div>
          </div>
        </div>
        <div className={styles.mobileAppColorSettingsWrapper}>
          <h1 className={`title-bold-medium ${styles.mobileAppFormGroupTitle}`}>
            {t('MOBILE_APP.FORM.COLOR_SCHEME')}
          </h1>
          <div className={styles.mobileAppFormGroup}>
            <ColorPicker
              key="primary"
              label={t('MOBILE_APP.FORM.PRIMARY_COLOR')}
              color={primary}
              setColor={setPrimary}
            />
            <ColorPicker
              key="secondary"
              label={t('MOBILE_APP.FORM.SECONDARY_COLOR')}
              color={secondary}
              setColor={setSecondary}
            />
            <ColorPicker
              key="tertiary"
              label={t('MOBILE_APP.FORM.TERTIARY_COLOR')}
              color={tertiary}
              setColor={setTertiary}
            />
          </div>
          <div className={styles.mobileAppFormGroup}>
            <ColorPicker
              key="background"
              label={t('MOBILE_APP.FORM.BACKGROUND_COLOR')}
              color={background}
              setColor={setBackground}
            />
          </div>
          <p className={`body-medium ${styles.mobileAppDescriptionText}`}>
            {t('MOBILE_APP.FORM.EXTRACT_COLORS.DESCRIPTION')}
          </p>
          {extractColorsStatus && (
            <div className={styles.extractColorsStatus}>
              <Alert
                severity={extractColorsStatus}
                message={t(EXTRACT_COLORS_MESSAGES[extractColorsStatus])}
                // Nothing to dismiss while the request is still out: closing it
                // would only hide the one thing that says the wait is expected.
                onClose={
                  isExtractingColors ? undefined : resetExtractColorsState
                }
              />
            </div>
          )}
          <ImageUploadButton
            imageShape="rectangle"
            label={t('MOBILE_APP.FORM.COLOR_SELECTOR')}
            slotId={IMAGE_SLOTS_KEYS.MOBILE_APP_COLOR_SELECTOR}
            imageUrl={
              mobileAppColorSlot?.croppedUrl ??
              mobileAppColorSlot?.originalUrl ??
              ''
            }
            imageSize={{ width: 608, height: 376 }}
            stencilSize={{ width: 432, height: 200 }}
          />
        </div>
      </div>
      <div className={styles.mobileAppFormFooter}>
        <div className={styles.mobileAppHint}>
          <i className="las la-lightbulb icon-big" />
          <p className={`body-small ${styles.mobileAppHintText}`}>
            {t('MOBILE_APP.FORM.HINT_TEXT')}
          </p>
        </div>

        <div className={styles.mobileAppFormButtonsContainer}>
          <Button
            label={t('COMMON.SAVE')}
            variant="contained"
            color="primary"
            onClick={handleSubmit}
          />
          <Button
            label={t('COMMON.CLEAR')}
            variant="contained"
            color="secondary"
            onClick={() => {
              reset()
              clearAll()
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default MobileAppForm
